import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ICON } from './icons';
import { Svg } from './Svg';
import { norm } from '@tg/domain';
import { Cav } from '../ui/Cav';
import { centerOn } from '../ui/ClientPicker';

export interface WhoItem {
  kind: 'client' | 'group';
  id: string;
  name: string;
  ini: string;
  photo?: string | null;
  sub: string;
  overLimit?: boolean;
}

/* Выбор клиента или группы (openClientPicker в nav.js): группы сверху, клиенты по алфавиту, поиск, клавиатура */
export function WhoPicker({ clients, groups, curId, onPick, onClose }: { clients: WhoItem[]; groups: WhoItem[]; curId?: string | null; onPick: (w: { clientId: string } | { groupId: string }) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [k, setK] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  /* Поповер — у кнопки, с которой открыли */
  const [pos] = useState(() => {
    const a = document.activeElement as HTMLElement | null;
    const r = a?.getBoundingClientRect();
    return r ? { left: r.left, top: r.bottom + window.scrollY + 6 } : { left: 300, top: 120 };
  });
  const qq = norm(q);
  const gr = useMemo(() => groups.filter((g) => !qq || norm(g.name).includes(qq)).sort((a, b) => a.name.localeCompare(b.name, 'ru')), [qq, groups]);
  const cl = useMemo(() => clients.filter((c) => !qq || norm(c.name + ' ' + c.sub).includes(qq)).sort((a, b) => a.name.localeCompare(b.name, 'ru')), [qq, clients]);
  const all = [...gr, ...cl];
  /* Открыли — список сразу на текущем клиенте или группе (посередине), стрелки идут от него */
  useLayoutEffect(() => {
    if (!curId) return;
    const i = all.findIndex((w) => w.id === curId);
    if (i >= 0) setK(i);
    centerOn(box.current, curId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const pick = (w: WhoItem) => onPick(w.kind === 'group' ? { groupId: w.id } : { clientId: w.id });
  useEffect(() => {
    const out = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && onClose();
    setTimeout(() => document.addEventListener('mousedown', out));
    return () => document.removeEventListener('mousedown', out);
  }, []);
  const row = (w: WhoItem, i: number) => (
    <button key={w.kind + w.id} className={'row ' + (w.id === curId ? 'cur ' : '') + (i === k ? 'on' : '')} data-cli={w.id} onClick={() => pick(w)}>
      <Cav ini={w.ini} photo={w.photo} grp={w.kind === 'group'} />
      <span className="cl-t">
        <b>{w.name}</b>
        <s>{w.sub}</s>
      </span>
    </button>
  );
  return createPortal(
    <div
      ref={box}
      className="sug clipick"
      style={pos}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
        else if (e.key === 'ArrowDown') (e.preventDefault(), setK((x) => Math.min(x + 1, all.length - 1)));
        else if (e.key === 'ArrowUp') (e.preventDefault(), setK((x) => Math.max(x - 1, 0)));
        else if (e.key === 'Enter' && all[k]) (e.preventDefault(), pick(all[k]));
      }}
    >
      <div className="cl-s">
        <Svg src={ICON.search} />
        <input autoFocus placeholder={groups.length ? 'Клиент или группа' : 'Имя клиента'} autoComplete="off" value={q} onChange={(e) => (setQ(e.target.value), setK(0))} />
      </div>
      <div className="cl-list">
        {all.length ? (
          <>
            {gr.length > 0 && <div className="cl-cap">Группы</div>}
            {gr.map((w, i) => row(w, i))}
            {gr.length > 0 && cl.length > 0 && <div className="cl-cap">Клиенты</div>}
            {cl.map((w, i) => row(w, gr.length + i))}
          </>
        ) : (
          <div className="cap">Никого не нашли</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
